import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';

/* =====================================================================
 * api.js — «Доходы и расходы» (АРМ). Реальное API, без мок-данных.
 *
 * Контроллер: @RequestMapping("/api/budget/arm") → /meta, /periods, /periods/{period}.
 *
 * Контракт бэкенда точно НЕ подтверждён. По опыту аналитики Spring отдаёт
 * вложенные { ru, kk } и id-ссылки, а не плоские *_ru/*_kk. Поэтому здесь:
 *   • tr() читает и { ru, kk }, и одноязычную строку, и плоские *_ru/*_kk;
 *   • список периодов разворачивается из "periods" | "items" | "content";
 *   • оставлены ВРЕМЕННЫЕ console.log [budget] — по ним доведём маппинг и удалим.
 *
 * Без ?? и ?. (react-app-rewired их не транспилирует).
 * ===================================================================== */

const META_ENDPOINT = '/api/budget/arm/meta';
const PERIODS_ENDPOINT = '/api/budget/arm/periods';

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const pick = (o, key) => {
  if (o === null || o === undefined) return undefined;
  return o[key] !== undefined ? o[key] : o[toCamel(key)];
};

const def = (v, d) => (v === null || v === undefined ? d : v);

// nested {ru,kk} → строка; одноязычная строка → как есть; иначе плоские *_ru/*_kk.
const tr = (o, base, lang) => {
  if (o === null || o === undefined) return undefined;
  const nested = pick(o, base);
  if (nested !== null && typeof nested === 'object') return nested[lang];
  if (typeof nested === 'string') return nested;
  return pick(o, `${base}_${lang}`);
};

// Строка справочника → плоский контракт, который ждёт страница
// (name_ru/name_kk, type/type_kk, row_id, sort_order, code).
function mapMetaRow(r) {
  return {
    row_id: pick(r, 'row_id'),
    sort_order: pick(r, 'sort_order'),
    code: def(pick(r, 'code'), ''),
    name_ru: tr(r, 'name', 'ru'),
    name_kk: tr(r, 'name', 'kk'),
    // раздел: type (ru) + type_kk; поддержим и вложенный {ru,kk}
    type: def(tr(r, 'type', 'ru'), pick(r, 'type')),
    type_kk: def(tr(r, 'type', 'kk'), pick(r, 'type_kk')),
  };
}

function mapMeta(data) {
  const rowsSrc = pick(data, 'rows') || {};
  return {
    years: pick(data, 'years') || [],
    years_with_data: pick(data, 'years_with_data') || pick(data, 'years') || [],
    months: mapMonths(pick(data, 'months')),
    rows: {
      income: (pick(rowsSrc, 'income') || []).map(mapMetaRow),
      expense: (pick(rowsSrc, 'expense') || []).map(mapMetaRow),
    },
  };
}

// Месяцы приходят массивом [{ n, ru, kk }] — приводим к { ru:[...], kk:[...] }.
// Раскладываем по n (1..12); если n нет — по порядку. Поддержим и готовый объект.
function mapMonths(src) {
  if (src && !Array.isArray(src) && (src.ru || src.kk)) {
    return { ru: src.ru || [], kk: src.kk || [] };
  }
  const ru = [];
  const kk = [];
  (Array.isArray(src) ? src : []).forEach((m, i) => {
    const n = pick(m, 'n');
    const idx = (n != null ? n : i + 1) - 1;
    ru[idx] = tr(m, 'name', 'ru') !== undefined ? tr(m, 'name', 'ru') : pick(m, 'ru');
    kk[idx] = tr(m, 'name', 'kk') !== undefined ? tr(m, 'name', 'kk') : pick(m, 'kk');
  });
  return { ru, kk };
}

// Итоги одного периода в перечне.
// Бэкенд: ключ периода — "period" (не "key"); число строк — "rows" (не "rows_count").
function mapPeriod(p) {
  return {
    month: pick(p, 'month'),
    key: def(pick(p, 'key'), pick(p, 'period')),
    entered: def(pick(p, 'entered'), false),
    rows_count: def(pick(p, 'rows_count'), pick(p, 'rows')),
    plan: pick(p, 'plan'),
    exec: pick(p, 'exec'),
    pct: pick(p, 'pct'),
  };
}

// Строка матрицы просмотра.
function mapPeriodDataRow(r) {
  return {
    row_id: pick(r, 'row_id'),
    plan: pick(r, 'plan'),
    exec: pick(r, 'exec'),
    // на случай row_id: null у исторических строк — наименование из ответа
    name_ru: tr(r, 'name', 'ru'),
    name_kk: tr(r, 'name', 'kk'),
  };
}

/* --------------------------------- Мета --------------------------------- */
export async function fetchBudgetMeta() {
  const res = await getNetwork().get(API_ROOT + META_ENDPOINT);
  // ⛔ ВРЕМЕННЫЙ ЛОГ — удалить после наладки.
  console.log('[budget] META RAW:', JSON.stringify(res.data));
  return mapMeta(res.data);
}

/* ------------------------ Перечень периодов года ------------------------ */
export async function fetchBudgetPeriods(side, year) {
  const res = await getNetwork().get(API_ROOT + PERIODS_ENDPOINT, { params: { side, year } });
  const data = res.data || {};
  // ⛔ ВРЕМЕННЫЙ ЛОГ — удалить после наладки.
  console.log('[budget] PERIODS RAW:', JSON.stringify(data));
  const list = pick(data, 'periods') || pick(data, 'items') || pick(data, 'content') || [];
  return { periods: list.map(mapPeriod) };
}

/* --------------------- Матрица одного периода (view) -------------------- */
export async function fetchBudgetPeriod(side, key) {
  const res = await getNetwork().get(`${API_ROOT + PERIODS_ENDPOINT}/${encodeURIComponent(key)}`, {
    params: { side },
  });
  const data = res.data || {};
  // ⛔ ВРЕМЕННЫЙ ЛОГ — удалить после наладки.
  console.log('[budget] PERIOD RAW:', JSON.stringify(data));
  const list = pick(data, 'rows') || pick(data, 'items') || [];
  return { rows: list.map(mapPeriodDataRow) };
}

/* ------------------------------- Создание ------------------------------- */
// payload: { side, year, month, rows: [ { row_id, plan, exec } ] }
export async function createBudgetPeriod(payload) {
  try {
    const res = await getNetwork().post(API_ROOT + PERIODS_ENDPOINT, payload);
    if (res && (res.status === 201 || res.status === 200)) {
      const d = res.data || {};
      return {
        side: def(pick(d, 'side'), payload.side),
        year: def(pick(d, 'year'), Number(payload.year)),
        month: def(pick(d, 'month'), Number(payload.month)),
        key: def(pick(d, 'key'), `${payload.month}.${payload.year}`),
      };
    }
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}
